'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useSignalStore from '@/stores/layout/signalStore';
import SurveyRenderer from '@/components/(features)/surveyWizard/(wizards)/component-1/SurveyRenderer';
import { SurveyBlock } from '@/types/survey';
import CloseFillIcon from 'remixicon-react/CloseFillIcon';
import Image from 'next/image';
import { LayoutProps } from '@/types/generalSettings';
import useStickyStore from '@/stores/layout/useStickyStore';
import { ShineBorder } from '@/components/magicui/shine-border';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import ShowGroupFieldsBeforeMobile = LayoutProps.ShowGroupFieldsBeforeMobile;
import { PopupTracking } from '@/components/(features)/surveyWizard/utils/popupTracking';

// Own chunk + CSS: loaded only when the gold webinar popup is used.
const GoldWebinarDialog = dynamic(
    () => import('@/components/(features)/surveyWizard/(wizards)/component-3/GoldWebinarDialog'),
    { ssr: false },
);

/** Alt text for the promo image (the panel gives no alt; an English slug was used before). */
const BANNER_ALT = 'پیشنهاد ویژه خانه سرمایه';

interface PopUpProps {
    survey?: Array<{
        block: SurveyBlock[];
    }>;
    showMobileInputField?: boolean;
    showGroupFieldsBeforeMobile?: ShowGroupFieldsBeforeMobile;
    banner?: LayoutProps.BannerItem | undefined;
    timer?: any;
    percent?: any;
    selectedWizard?: string | undefined;
    tracking?: PopupTracking;
}

interface BannerHtmlContentProps {
    subtitle?: string;
    title?: string;
    button?: string;
}

const PopUpSurvey: React.FC<PopUpProps> = ({
    survey,
    banner,
    showMobileInputField,
    showGroupFieldsBeforeMobile,
    timer,
    percent,
    selectedWizard,
    tracking,
}) => {
    const pathname = usePathname();
    const isGoldWebinar = selectedWizard === 'goldWebinar';
    const autoOpenTimeoutRef = useRef<number | null>(null);
    const theme = useTheme();
    const [isDesktop, setIsDesktop] = useState<boolean>(false);
    const [showBannerTrigger, setShowBannerTrigger] = useState(false);

    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const checkDesktop = () => {
                setIsDesktop(window.innerWidth >= 768);
            };
            checkDesktop();
            window.addEventListener('resize', checkDesktop, { passive: true });
            return () => window.removeEventListener('resize', checkDesktop);
        }
    }, []);

    useEffect(() => {
        const id = window.setTimeout(() => setShowBannerTrigger(true), 1500);
        return () => clearTimeout(id);
    }, []);

    const { popupStatus, isOpen, setIsOpen, setHideTransition, setAutoOpened } = useSignalStore();

    const { isStickyVisible, setIsStickyVisible } = useStickyStore();

    const isDynamicSlug = (path: string): boolean => {
        const dynamicSlugPatterns: RegExp[] = [
            /^\/exchange\/.+/,
            /^\/broker\/.+/,
            /^\/prop\/.+/,
            /^\/gold-platforms\/.+/,
        ];
        return dynamicSlugPatterns.some((pattern) => pattern.test(path));
    };

    useEffect(() => {
        const staticSlugs = ['/start', '/nokhbegan', '/trade-webinar-24/'];
        if (staticSlugs.includes(pathname) || isDynamicSlug(pathname)) {
            setIsStickyVisible(true);
        } else {
            setIsStickyVisible(false);
        }
    }, [pathname, setIsStickyVisible]);

    useEffect(() => {
        if (popupStatus === 'done') {
            setIsOpen(false);
            setHideTransition(true);
        } else {
            setHideTransition(false);
        }
    }, [popupStatus, setIsOpen, setHideTransition]);

    useEffect(() => {
        if (
            isOpen &&
            pathname !== '/start/' &&
            pathname !== '/nokhbegan/' &&
            pathname !== '/best-exchange-landing/' &&
            pathname !== '/symbol/usdt/' &&
            pathname !== '/trade-webinar-24/'
        ) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [isOpen, pathname]);

    const [showExitConfirmation, setShowExitConfirmation] = useState(false);

    const handleCloseClick = () => {
        setShowExitConfirmation(true);
    };

    const handleConfirmKeepPopup = () => {
        setShowExitConfirmation(false);
    };

    const handleConfirmClosePopup = () => {
        setShowExitConfirmation(false);
        setIsOpen(false);
        setAutoOpened(false);

        const expirationTime = Date.now() + 24 * 60 * 60 * 1000;
        localStorage.setItem(
            'popupClosed',
            JSON.stringify({ closed: true, expires: expirationTime }),
        );
    };

    const [hasScrolled, setHasScrolled] = useState(false);

    const handleScroll = useCallback(() => {
        const scrolled = window.scrollY > 150;
        if (scrolled && !hasScrolled) {
            setHasScrolled(true);
        }
    }, [hasScrolled]);

    useEffect(() => {
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const triggerAutoOpen = useCallback(() => {
        if (hasScrolled) {
            autoOpenTimeoutRef.current = window.setTimeout(() => {
                setAutoOpened(true);
                setIsOpen(true);
            }, 10000);
        }
    }, [hasScrolled, setIsOpen, setAutoOpened]);

    useEffect(() => {
        const popupClosedData = localStorage.getItem('popupClosed');

        if (popupClosedData) {
            try {
                const { closed, expires } = JSON.parse(popupClosedData);
                if (closed && expires && Date.now() < expires) {
                    return;
                }
            } catch (error) {
                console.error('Error parsing popupClosed data:', error);
            }
        }

        triggerAutoOpen();

        return () => {
            if (autoOpenTimeoutRef.current) {
                clearTimeout(autoOpenTimeoutRef.current);
            }
        };
    }, [triggerAutoOpen]);

    if (
        pathname === '/start/' ||
        pathname === '/nokhbegan/' ||
        pathname === '/best-exchange-landing/' ||
        pathname === '/symbol/usdt/' ||
        pathname === '/trade-webinar-24/'
    ) {
        return null;
    }
    const BannerHtmlContent: React.FC<BannerHtmlContentProps> = ({ subtitle, title, button }) => (
        <div className='w-full absolute inset-0 flex justify-between p-4 z-[1] items-center'>
            <div className='flex w-fit flex-col items-end'>
                <p className='text-xs text-black/60'>{subtitle}</p>
                <p className='text-black font-bold text-base'>{title}</p>
            </div>
            {/* a span: the whole banner is already the link / button */}
            <span className='px-3 py-1 rounded-lg bg-gold text-black hover:brightness-110'>
                {button}
            </span>
        </div>
    );

    // Exit-intent confirmation, shown over either popup.
    const exitConfirmation = showExitConfirmation && (
        <div
            className={`absolute inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-6 ${isGoldWebinar ? 'overflow-hidden rounded-[28px] max-[520px]:rounded-[24px]' : ''}`}
        >
            <div className='bg-white dark:bg-primary-dark rounded-2xl p-6 w-full max-w-[360px] flex flex-col items-center shadow-2xl border border-gray-150 dark:border-white/10'>
                <div className='bg-primary/10 dark:bg-white/10 p-4 rounded-full text-[#EEAC43] mb-4'>
                    <svg
                        className='size-10'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5a2 2 0 10-2 2h2zm0 0h4l1 3H7l1-3h4zm-7 3h14v10a2 2 0 01-2 2H7a2 2 0 01-2-2V11z'
                        />
                    </svg>
                </div>
                <h3 className='text-base font-bold text-center text-gray-900 dark:text-white mb-2'>
                    آیا مطمئن هستید این هدیه را الان نمیخواهید؟
                </h3>
                <p className='text-xs text-gray-500 dark:text-gray-400 text-center mb-6'>
                    این پیشنهاد برای مدت محدود فعال است.
                </p>
                <div className='flex flex-col gap-2 w-full'>
                    <button
                        type='button'
                        onClick={handleConfirmKeepPopup}
                        className='w-full py-3 px-6 bg-gradient-to-r from-[#C07808] to-[#EEAC43] hover:brightness-105 text-white font-bold rounded-xl text-sm transition-all'
                    >
                        هدیه را می‌خواهم
                    </button>
                    <button
                        type='button'
                        onClick={handleConfirmClosePopup}
                        className='w-full py-3 px-6 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-gray-300 font-medium rounded-xl text-sm transition-all'
                    >
                        فعلاً نمی‌خواهم
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {showBannerTrigger &&
                !isStickyVisible &&
                banner?.show &&
                !isDesktop &&
                (banner?.type === 'porsline' ? (
                    <div
                        // slides up into view once (CSS, see .khs-slide-up)
                        onClick={() => setIsOpen(true)}
                        role='button'
                        tabIndex={0}
                        aria-haspopup='dialog'
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setIsOpen(true);
                            }
                        }}
                        className='shiningTopbar shine-strip fixed bottom-0 z-20 w-full cursor-pointer overflow-hidden md:hidden khs-slide-up'
                    >
                        <Image
                            src={
                                banner?.format === 'image'
                                    ? banner.image?.sourceUrl ||
                                      '/landing-new-year/desktop-topbar.png'
                                    : banner?.html?.image?.sourceUrl ||
                                      '/landing-new-year/desktop-topbar.png'
                            }
                            alt={BANNER_ALT}
                            width={1800}
                            // 1. FIX: Changed height to match the actual banner aspect ratio (1771x275)
                            height={280}
                            // 2. FIX: Tells Next.js to only serve mobile-sized images!
                            sizes='100vw'
                            className='max-h-16 object-cover w-full'
                        />
                        {banner?.format === 'html' && (
                            <BannerHtmlContent
                                subtitle={banner?.html?.subtitle}
                                title={banner?.html?.title}
                                button={banner?.html?.button}
                            />
                        )}
                    </div>
                ) : (
                    <div
                        // slides up into view once (CSS, see .khs-slide-up)
                        className='fixed bottom-0 left-0 right-0 z-20 md:hidden khs-slide-up'
                    >
                        <Link
                            prefetch={false}
                            href={banner?.link?.url || '#'}
                            target={'_blank'}
                            rel='noopener'
                            className='shiningTopbar shine-strip relative block w-full overflow-hidden'
                        >
                            <Image
                                src={
                                    banner?.format === 'image'
                                        ? banner.image?.sourceUrl ||
                                          '/landing-new-year/desktop-topbar.png'
                                        : banner?.html?.image?.sourceUrl ||
                                          '/landing-new-year/desktop-topbar.png'
                                }
                                alt={BANNER_ALT}
                                width={1800}
                                height={280}
                                sizes='100vw'
                                className='max-h-16 object-cover w-full'
                            />
                            {banner?.format === 'html' && (
                                <BannerHtmlContent
                                    subtitle={banner?.html?.subtitle}
                                    title={banner?.html?.title}
                                    button={banner?.html?.button}
                                />
                            )}
                        </Link>
                    </div>
                ))}
            {isMounted && (
                <Dialog open={isOpen}>
                    {/* Accessible name without an <h2> in every page's outline (it read as a heading «پاپ آپ») */}
                    <DialogTitle asChild>
                        <span className='sr-only'>پیشنهاد ویژه خانه سرمایه</span>
                    </DialogTitle>
                    {isGoldWebinar ? (
                        // Own chunk + CSS, loaded only when this popup is used (its 21KB of CSS
                        // was render-blocking on every page).
                        <GoldWebinarDialog
                            content={showGroupFieldsBeforeMobile}
                            tracking={tracking}
                            survey={survey}
                            timer={timer}
                            percent={percent}
                            onClose={handleCloseClick}
                            onFinish={handleConfirmClosePopup}
                        >
                            {exitConfirmation}
                        </GoldWebinarDialog>
                    ) : (
                        <DialogContent
                            aria-labelledby='popupDesktop'
                            className={`p-0 !rounded-xl flex flex-col dark:border-white/20 items-center justify-start w-[90%] md:max-w-[480px] max-h-[95vh] md:max-h-[90vh] ${selectedWizard === 'wizard2' ? 'bg-[#01003a]' : 'bg-white dark:bg-primary-dark '} overflow-y-hidden`}
                        >
                            <>
                                <ShineBorder
                                    shineColor={theme.theme === 'dark' ? 'white' : '#EEAC43'}
                                />

                                <button
                                    aria-label='close wizard modal'
                                    className={
                                        'absolute top-2 left-2 bg-white dark:bg-primary-dark hover:brightness-90 dark:hover:brightness-110 transition z-50 rounded-lg cursor-pointer'
                                    }
                                    onClick={handleCloseClick}
                                >
                                    <CloseFillIcon
                                        size={30}
                                        className={'text-primary/60 dark:text-white/60'}
                                    />
                                </button>
                                <SurveyRenderer
                                    handleConfirmClosePopup={handleConfirmClosePopup}
                                    tracking={tracking}
                                    selectedWizard={selectedWizard}
                                    percent={percent}
                                    timer={timer}
                                    survey={survey}
                                    showMobileInputField={showMobileInputField}
                                    showGroupFieldsBeforeMobile={showGroupFieldsBeforeMobile}
                                />
                            </>
                            {exitConfirmation}
                        </DialogContent>
                    )}
                </Dialog>
            )}
        </>
    );
};

export default PopUpSurvey;
